// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { UserDO } from 'wdna-components';
import { DashComponent, DashboardLauncher, DashboardReload } from 'wdna-components/lib/dashboards';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError } from 'wdna-components/lib/helpers/notification';

/* Types */
import { FilterContext } from '../../../../types';

/* Sidebar */
import MainFilter from '../components/addons/MainFilter';
import DrilldownLoad from '../components/addons/DrilldownLoad';

import { waste } from '../../../sensors/requests';

/* Components */
const MainContainer: React.LazyExoticComponent<any> = React.lazy(() => import('../components/dashboard/MainFrame'));

const DEFAULT_DASHBOARD_TIMEOUT: number = 1200000;
const dashConfig: DashComponent[] = [
  {
    component: MainContainer,
    props: {
      title: '',
      container: 'PET',
      className: 'waste-container',
    },
  },
];

type Props = any;

interface State {
  mainFilter?: any;
  sidebar?: React.ReactNode[];
  dashboardData: any[];
}

class MainDashboard extends React.Component<Props, State> {
  private pid: string;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.state = {
      sidebar: undefined,
      dashboardData: [],
    };
  }

  public componentDidMount(): void {
    this.fetchData();
  }

  public componentDidUpdate(prevProps: Props): void {
    if (this.props !== prevProps) {
      this.fetchData();
    }
  }

  public updateParent = (data: State, cb?: (() => void)): void => {
    this.setState({ ...data }, () => {
      if (typeof cb === 'function') cb();
    });
  }

  public fetchData = (): void => {
    window.store.dispatch(initProcess(this.pid));
    try {
      this.processSidebarConfig();
    } catch (error: any) {
      messageError('Error', error.message);
    }

    setTimeout(() => {
      window.store.dispatch(finishProcess(this.pid));
    }, 1000);
  };

  public processSidebarConfig = (): void => {
    const sidebar: React.ReactNode[] = [(): JSX.Element => <MainFilter requestFunc={this.getDashboardData} autoRefresh />, DrilldownLoad];

    this.setState({
      sidebar,
    });
  };

  public getDashboardData = async (): Promise<any> => {
    const filter: any = this.state.mainFilter;
    const { date1 } = filter.arrFilterDate[0];
    const { date2 } = filter.arrFilterDate[filter.arrFilterDate.length - 1];

    const from: string = date1;
    let to: string = date2;

    const date: Date = new Date(to);
    const nextDate: Date = new Date(date.setDate(date.getDate() + 1));
    to = moment(nextDate).format('YYYY-MM-DD');

    const { company, verified, active } = filter;
    const granularity: Record<string, string> = { '24h': 'DAY', '7d': 'WEEK', '30d': 'MONTH' };
    const group: string = granularity[filter.groupDateTime];

    const reqDashboard: Record<string, any> = { group, from, to: date2 };
    const reqStatistics: Record<string, any> = { from, to };
    if (company) {
      reqDashboard.companyId = company;
      reqStatistics.companyId = company;
    }

    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userType: string = (user && user.type) || 'cli';
    if (userType === 'cli') {
      reqDashboard.verified = true;
      reqStatistics.verified = true;
      reqDashboard.active = true;
      reqStatistics.active = true;
    } else {
      if (Number(verified) === 1) {
        reqDashboard.verified = true;
        reqStatistics.verified = true;
      }
      if (Number(active) === 1) {
        reqDashboard.active = true;
        reqStatistics.active = true;
      }
    }

    const [graphData, circleData] = await Promise.all([
      waste('dashboard', undefined, reqDashboard),
      waste('statistics', undefined, reqStatistics),
    ]);

    return { graphData, circleData };
  };

  public render(): React.ReactNode {
    const { sidebar } = this.state;
    if (!dashConfig || !sidebar) return null;

    return (
      <FilterContext.Provider value={{ parentState: this.state, updateParent: this.updateParent }}>
        <DashboardLauncher config={dashConfig} sidebar={sidebar} hiddenSidebar />
      </FilterContext.Provider>
    );
  }
}

const View: React.MemoExoticComponent<any> = React.memo((props: Props): JSX.Element => {
  return (
    <DashboardReload timeOut={DEFAULT_DASHBOARD_TIMEOUT}>
      <MainDashboard {...props} />
    </DashboardReload>
  );
});

export default View;
